import { SelectDropdown } from '../shared/SelectDropdown';
import { ClientEmailDelivery, ClientEmailOption, CopyTooltip, ModalTitle } from '@overlayqa/shared/ui';
import { useEffect, useRef, useState } from 'react';
import { Share01Icon, SentIcon, Link01Icon, PlusSignIcon, Tick02Icon, Copy01Icon, Note01Icon, ViewOffSlashIcon, LaptopPhoneSyncIcon, ArrowDown01Icon } from '@hugeicons/core-free-icons';
import { ToggleRow } from '../ui/ToggleRow';
import { WELCOME_NOTE_MAX, WELCOME_NOTE_PLACEHOLDER } from './ClientReviewDefaults';
import { HugeiconsIcon } from '@hugeicons/react';
import type { ClientEmailInvite } from '@overlayqa/shared';
import { capture } from '@/lib/analytics';
import { cleanClientEmails } from './ClientEmailsField';
import { useClientReviewInvitesEnabled } from '../../hooks/useClientReviewInvites';
import { useModalA11y } from '@/hooks/useModalA11y';
import { useCreateClientReview } from '../../hooks/useClientReviews';
import { ApiError } from '../../lib/api/apiError';
import { classifyUpgradeBlock } from '../../lib/api/upgradeBlock';
import { isHandledHttpStatus } from '../../lib/api/errorLogLevel';
import { copyText } from '../../lib/copyText';
import { FIGMA_DESIGN_LINK_REVIEW_ERROR, isFigmaDesignArtifactUrl } from '@overlayqa/shared';
import { log } from '@/lib/logger';
import { ModalPortal } from '@/components/shared/ModalPortal';
import { EMPTY_PAGE_URL } from '../../lib/clientReviewInitialUrl';

// Mirrors the server's createClientReviewSchema bounds (clientReviews.ts):
// out-of-range custom viewports previously relied on the native min/max
// bubble alone — invisible in replays and silently bypassed by the
// `parseInt || 1440` fallback for empty input (OQA-13).
const VIEWPORT_WIDTH_MIN = 320;
const VIEWPORT_WIDTH_MAX = 3840;
const VIEWPORT_HEIGHT_MIN = 320;
const VIEWPORT_HEIGHT_MAX = 2160;

interface ClientReviewCreateModalProps {
  projectId: string;
  onClose: () => void;
  /**
   * Where the Page URL field starts: the project's newest review URL, else
   * `https://` + its primary domain (resolveClientReviewInitialUrl, computed
   * by ClientReviewSection, which owns both queries). May arrive after mount
   * when the user clicks Share client link before those queries land; it is adopted
   * then ONLY while the field is still pristine, so it can never overwrite
   * something the user typed. Absent: the field opens on `https://` as before.
   */
  initialUrl?: string;
  /** The project's Sharing defaults; Guest options start from these (adopted late while untouched). */
  guestDefaults?: GuestDefaults;
}

export interface GuestDefaults {
  welcomeEnabled: boolean;
  welcomeNote: string;
  privateComments: boolean;
  allowResponsiveViews: boolean;
}

const NO_DEFAULTS: GuestDefaults = { welcomeEnabled: false, welcomeNote: '', privateComments: false, allowResponsiveViews: true };

type ExpiryOption = 'never' | '7' | '30' | '90';

type ViewportPreset = 'desktop' | 'laptop' | 'tablet' | 'mobile' | 'custom';

const VIEWPORT_PRESETS: Record<Exclude<ViewportPreset, 'custom'>, { width: number; height: number; label: string }> = {
  desktop: { width: 1440, height: 900, label: 'Desktop (1440 x 900)' },
  laptop: { width: 1280, height: 800, label: 'Laptop (1280 x 800)' },
  tablet: { width: 768, height: 1024, label: 'Tablet (768 x 1024)' },
  mobile: { width: 375, height: 812, label: 'Mobile (375 x 812)' },
};

function expiryToDate(option: ExpiryOption): string | undefined {
  if (option === 'never') return undefined;
  const days = parseInt(option, 10);
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

export function ClientReviewCreateModal({ projectId, onClose, initialUrl, guestDefaults = NO_DEFAULTS }: ClientReviewCreateModalProps) {
  const createReview = useCreateClientReview();
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(true, dialogRef, () => handleClose());

  const [pageUrl, setPageUrl] = useState(initialUrl ?? EMPTY_PAGE_URL);
  // False the moment the user edits the field or a create succeeds. Gates the
  // late-initialUrl adoption below: a prop that lands after typing began, or
  // after "Create another link" re-seeded the form, must not clobber it.
  const fieldPristineRef = useRef(true);
  // The URL the last successful create used, so "Create another link" opens on
  // it (it is now the project's last used URL) without waiting for the reviews
  // list to refetch and hand a new initialUrl down.
  const [lastCreatedUrl, setLastCreatedUrl] = useState<string | null>(null);
  const [mode, setMode] = useState<'live' | 'snapshot'>('snapshot');
  const [expiry, setExpiry] = useState<ExpiryOption>('never');
  const [allowResponsiveViews, setAllowResponsiveViews] = useState(guestDefaults.allowResponsiveViews);
  const [welcomeOn, setWelcomeOn] = useState(guestDefaults.welcomeEnabled);
  const [welcomeNote, setWelcomeNote] = useState(guestDefaults.welcomeNote);
  const [privateComments, setPrivateComments] = useState(guestDefaults.privateComments);
  const [guestOptionsOpen, setGuestOptionsOpen] = useState(false);
  // False once the user touches a guest option, so late project defaults never overwrite a choice.
  const guestPristineRef = useRef(true);
  const [viewport, setViewport] = useState<ViewportPreset>('desktop');
  const [customWidth, setCustomWidth] = useState('1440');
  const [customHeight, setCustomHeight] = useState('900');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [viewportError, setViewportError] = useState<string | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const copyTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(copyTimer.current), []);
  // One input row per client email; '' rows are ignored on submit.
  const [title, setTitle] = useState('');
  const [clientEmails, setClientEmails] = useState<string[]>(['']);
  const [emailInvalidIndex, setEmailInvalidIndex] = useState<number | null>(null);
  const [invites, setInvites] = useState<ClientEmailInvite[]>([]);
  // While invites are switched off the Client emails field is hidden entirely (Emily, 2026-10-12).
  const invitesOn = useClientReviewInvitesEnabled();
  const hasClientEmail = invitesOn && clientEmails.some(e => e.trim() !== '');

  useEffect(() => {
    if (!guestPristineRef.current) return;
    setAllowResponsiveViews(guestDefaults.allowResponsiveViews);
    setWelcomeOn(guestDefaults.welcomeEnabled);
    setWelcomeNote(guestDefaults.welcomeNote);
    setPrivateComments(guestDefaults.privateComments);
  }, [guestDefaults.allowResponsiveViews, guestDefaults.welcomeEnabled, guestDefaults.welcomeNote, guestDefaults.privateComments]);
  const touchGuest = () => { guestPristineRef.current = false; };
  const guestSummary = [
    welcomeOn ? 'Welcome note on' : 'No welcome note',
    privateComments ? 'Private comments' : 'Shared comments',
    allowResponsiveViews ? 'Responsive views' : 'Fixed width',
  ].join(' · ');

  useEffect(() => {
    if (!fieldPristineRef.current) return;
    if (!initialUrl || initialUrl === EMPTY_PAGE_URL) return;
    log.info('[ClientReviewCreateModal] Adopting initialUrl', { initialUrl });
    setPageUrl(initialUrl);
  }, [initialUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUrlError(null);
    setFormError(null);
    setViewportError(null);

    const trimmed = pageUrl.trim();
    // A bare scheme (the pre-filled https://) counts as empty, not a bad URL.
    if (!trimmed || trimmed === EMPTY_PAGE_URL || trimmed === 'http://') {
      setUrlError('Page URL is required');
      return;
    }
    try {
      new URL(trimmed);
    } catch {
      setUrlError('Enter a valid URL including https://');
      return;
    }
    // A figma.com design link can never render as a reviewable page: live
    // mode proxies an app shell that cannot boot, snapshot mode captures that
    // shell (blank-review incident 2026-08-12). Same guard as LiveReviewModal.
    if (isFigmaDesignArtifactUrl(trimmed)) {
      setUrlError(FIGMA_DESIGN_LINK_REVIEW_ERROR);
      return;
    }

    const cleaned = cleanClientEmails(invitesOn ? clientEmails : []);
    if (!cleaned.ok) {
      setEmailInvalidIndex(cleaned.invalidIndex);
      return;
    }

    let vpDims: { width: number; height: number };
    if (viewport === 'custom') {
      // Visible inline validation — never silently clamp or fall back. The
      // native min/max bubble is the only other guard and it's invisible in
      // replays; an empty field used to silently send 1440x900 (OQA-13).
      const width = Number(customWidth);
      const height = Number(customHeight);
      if (!customWidth.trim() || !Number.isInteger(width) || width < VIEWPORT_WIDTH_MIN || width > VIEWPORT_WIDTH_MAX) {
        setViewportError(`Width must be a whole number between ${VIEWPORT_WIDTH_MIN} and ${VIEWPORT_WIDTH_MAX}`);
        return;
      }
      if (!customHeight.trim() || !Number.isInteger(height) || height < VIEWPORT_HEIGHT_MIN || height > VIEWPORT_HEIGHT_MAX) {
        setViewportError(`Height must be a whole number between ${VIEWPORT_HEIGHT_MIN} and ${VIEWPORT_HEIGHT_MAX}`);
        return;
      }
      vpDims = { width, height };
    } else {
      vpDims = VIEWPORT_PRESETS[viewport];
    }

    try {
      const result = await createReview.mutateAsync({
        projectId,
        pageUrl: trimmed,
        mode,
        expiresAt: expiryToDate(expiry),
        viewportWidth: vpDims.width,
        viewportHeight: vpDims.height,
        allowResponsiveViews,
        ...(cleaned.emails.length ? { clientEmails: cleaned.emails } : {}),
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(welcomeOn && welcomeNote.trim() ? { welcomeNote: welcomeNote.trim() } : {}),
        privateComments,
      });
      log.info('[ClientReviewCreateModal] Created review', { id: result.id, inviteCount: result.invites?.length ?? 0 });
      setInvites(result.invites ?? []);
      fieldPristineRef.current = false;
      setLastCreatedUrl(trimmed);
      setGeneratedUrl(result.shareUrl);
    } catch (err) {
      // Surface the server's message (e.g. plan-limit reached) rather than a
      // generic "try again" that hides why the create failed.
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Failed to create review link. Please try again.';

      // A handled refusal (plan/trial gate 403, expired session 401) is an
      // expected outcome, not a crash. log.error reaches console.error in prod,
      // which PostHog capture_console_errors files as a $exception, so a handled
      // refusal shows up as a fake crash in Error Tracking. Warn keeps
      // DEBUG_OVERLAYQA visibility without the $exception; genuinely unexpected
      // failures (5xx, network) stay at error so real regressions stay
      // prod-visible. Mirrors client.ts's handled-status branch.
      const status = err instanceof ApiError ? err.status : undefined;
      if (status !== undefined && isHandledHttpStatus(status)) {
        log.warn('[ClientReviewCreateModal] Create refused', { err });
      } else {
        log.error('[ClientReviewCreateModal] Failed to create review', { err });
      }

      // A plan/trial gate is not a URL problem: route it to the form-level alert
      // (the global 403 interceptor opens the upgrade modal alongside) instead
      // of painting a valid URL red. classifyUpgradeBlock is the single source
      // of truth for which codes are gates (TRIAL_EXPIRED / PLAN_UPGRADE_REQUIRED
      // / plan_limit_exceeded), so a new server gate code routes correctly here
      // without another hand-listed check.
      const isUpgradeGate =
        err instanceof ApiError &&
        classifyUpgradeBlock(err.code, err.message, err.details) !== null;
      if (isUpgradeGate) {
        setFormError(message);
      } else {
        setUrlError(message);
      }
    }
  };

  const handleCopy = async () => {
    if (!generatedUrl) return;
    clearTimeout(copyTimer.current);
    setCopyState('idle');
    // copyText never fails silently: the async Clipboard API rejects on
    // unfocused documents / denied permission, and the old .then-only
    // handler showed nothing on that path (OQA-11).
    const ok = await copyText(generatedUrl);
    setCopyState(ok ? 'copied' : 'failed');
    copyTimer.current = setTimeout(() => setCopyState('idle'), 2000);
  };

  const handleClose = () => {
    onClose();
  };

  // Reset to a fresh form so the user can create another link without
  // reopening the modal. Mode / viewport / expiry stay as the last choice, and
  // the URL opens on the one just created: it is the project's last used URL
  // now, and a second link for the same page (other mode, other viewport) is
  // the common case.
  const handleCreateAnother = () => {
    setGeneratedUrl(null);
    setPageUrl(lastCreatedUrl ?? initialUrl ?? EMPTY_PAGE_URL);
    setUrlError(null);
    setViewportError(null);
    setCopyState('idle');
    setInvites([]);
    setClientEmails(['']);
    setTitle('');
    setEmailInvalidIndex(null);
  };

  const overlay = (
    <div
      ref={dialogRef}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-review-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl overflow-hidden flex flex-col max-h-[calc(100vh-2rem)]">
        {/* Header */}
        <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between shrink-0">
          <ModalTitle icon={Share01Icon} id="client-review-modal-title" className="oq-dialog__title">Share client link</ModalTitle>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 shrink-0"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {generatedUrl ? (
          /* Success state: show generated link */
          <div className="px-6 pt-4 pb-5 min-h-0 overflow-y-auto">
            {invites.length > 0 ? (
              <ClientEmailDelivery invites={invites} className="modal-intro mb-3" />
            ) : (
              <p className="modal-intro mb-3">Your client link is ready. Share it with your client.</p>
            )}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 border border-gray-200 mb-5">
              <span className="text-sm text-gray-700 truncate flex-1 min-w-0">
                {generatedUrl}
              </span>
              <CopyTooltip label="Copy link" copied={copyState === 'copied'} failed={copyState === 'failed'}><button
                type="button"
                onClick={handleCopy}
                className="shrink-0 btn-secondary btn-sm"
              >
                <HugeiconsIcon icon={Copy01Icon} size={14} color="currentColor" className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                <span className="text-cap-trim">{copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Copy failed' : 'Copy'}</span>
              </button></CopyTooltip>
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                className="btn-secondary whitespace-nowrap"
                onClick={handleCreateAnother}
              >
                <HugeiconsIcon icon={PlusSignIcon} size={16} color="currentColor" className="w-4 h-4 mr-2" aria-hidden="true" />
                <span className="text-cap-trim">Create another link</span>
              </button>
              <button
                type="button"
                className="btn-primary whitespace-nowrap"
                onClick={handleClose}
              >
                <HugeiconsIcon icon={Tick02Icon} size={16} color="currentColor" className="w-4 h-4 mr-2" aria-hidden="true" />
                <span className="text-cap-trim">Done</span>
              </button>
            </div>
          </div>
        ) : (
          /* Form state. noValidate: every constraint is enforced in
             handleSubmit with a VISIBLE inline error (urlError/viewportError).
             The native bubble is invisible in replays and inconsistent across
             drivers — min/max attrs stay only for the spinner affordance. */
          <form onSubmit={handleSubmit} noValidate className="px-6 pt-4 pb-5 overflow-y-auto">
            <p className="modal-intro mb-4">Collect feedback from your client. Anyone with the link can review this page without an account.</p>
            <fieldset className="mb-5">
              <label htmlFor="reviewTitle" className="field-label">Title (optional)</label>
              <input
                id="reviewTitle"
                type="text"
                value={title}
                maxLength={200}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Homepage redesign"
                className="input input-lg w-full"
              />
              <p className="field-hint mt-1">Shown in the list and in emails to your client.</p>
            </fieldset>

            {/* Page URL */}
            <fieldset className="mb-5">
              <label
                htmlFor="reviewPageUrl"
                className="field-label"
              >
                Page URL <span className="text-red-500">*</span>
              </label>
              <input
                id="reviewPageUrl"
                type="url"
                value={pageUrl}
                onChange={(e) => {
                  fieldPristineRef.current = false;
                  setPageUrl(e.target.value);
                  setUrlError(null);
                }}
                placeholder="https://example.com/page"
                className={`input input-lg w-full${urlError ? ' input-error' : ''}`}
                autoFocus
                required
                aria-invalid={!!urlError}
                aria-describedby={urlError ? 'reviewPageUrl-error' : undefined}
              />
              {urlError && (
                <p id="reviewPageUrl-error" className="mt-1 text-xs text-red-600" role="alert">{urlError}</p>
              )}
            </fieldset>

            {/* Client emails sit right under the page, visible and optional (Emily, 2026-11-01). */}
            {invitesOn && (
              <ClientEmailOption
                disabled={createReview.isPending}
                rows={clientEmails}
                onChange={rows => { setClientEmails(rows); setEmailInvalidIndex(null); }}
                invalidIndex={emailInvalidIndex}
                surface="create"
                onEvent={capture}
              />
            )}

            {/* Mode */}
            <fieldset className="mb-5">
              <legend className="field-label">
                Mode
              </legend>
              <div className="flex flex-col gap-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="reviewMode"
                    value="snapshot"
                    checked={mode === 'snapshot'}
                    onChange={() => setMode('snapshot')}
                    className="mt-1 accent-primary-600"
                  />
                  <span>
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">Snapshot</span>
                      <span className="inline-flex items-center px-2 py-1 rounded text-[10px] font-medium bg-primary-50 text-primary-700 border border-primary-200">
                        Recommended
                      </span>
                    </span>
                    <span className="block text-xs text-gray-500">Capture a frozen copy of the page for review</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="reviewMode"
                    value="live"
                    checked={mode === 'live'}
                    onChange={() => setMode('live')}
                    className="mt-1 accent-primary-600"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-800">Live site</span>
                    <span className="block text-xs text-gray-500">Client sees the current live version of the page</span>
                  </span>
                </label>
              </div>
            </fieldset>

            {/* Viewport */}
            <fieldset className="mb-5">
              <label
                htmlFor="reviewViewport"
                className="field-label"
              >
                Viewport size
              </label>
              <SelectDropdown
                id="reviewViewport"
                value={viewport}
                onChange={(value) => setViewport(value as ViewportPreset)}
                size="lg" className="w-full"
                options={[
                  ...Object.entries(VIEWPORT_PRESETS).map(([value, preset]) => ({
                    value, label: preset.label, leading: <ViewportIcon kind={viewportIconForWidth(preset.width)} />,
                  })),
                  { value: 'custom', label: 'Custom', leading: <ViewportIcon kind="frame" /> },
                ]}
              />
              {viewport === 'custom' && (
                <div className="flex gap-2 mt-2">
                  <input
                    type="number"
                    value={customWidth}
                    onChange={(e) => { setCustomWidth(e.target.value); setViewportError(null); }}
                    placeholder="Width"
                    min={VIEWPORT_WIDTH_MIN}
                    max={VIEWPORT_WIDTH_MAX}
                    className="input input-lg w-full"
                    aria-invalid={!!viewportError}
                    aria-describedby={viewportError ? 'reviewViewport-error' : undefined}
                  />
                  <span className="self-center text-gray-400 text-sm">x</span>
                  <input
                    type="number"
                    value={customHeight}
                    onChange={(e) => { setCustomHeight(e.target.value); setViewportError(null); }}
                    placeholder="Height"
                    min={VIEWPORT_HEIGHT_MIN}
                    max={VIEWPORT_HEIGHT_MAX}
                    className="input input-lg w-full"
                    aria-invalid={!!viewportError}
                    aria-describedby={viewportError ? 'reviewViewport-error' : undefined}
                  />
                </div>
              )}
              {viewportError && (
                <p id="reviewViewport-error" className="mt-1 text-xs text-red-600" role="alert">{viewportError}</p>
              )}
              <p className="field-hint mt-1">
                {allowResponsiveViews ? "The review opens at this width" : "The reviewer will see the page at this width"}
              </p>
            </fieldset>


            {/* Expires */}
            <fieldset className="mb-5">
              <label
                htmlFor="reviewExpiry"
                className="field-label"
              >
                Expires
              </label>
              <SelectDropdown
                id="reviewExpiry"
                value={expiry}
                onChange={(value) => setExpiry(value as ExpiryOption)}
                size="lg" className="w-full"
                options={[{ value: 'never', label: 'Never' }, { value: '7', label: '7 days' }, { value: '30', label: '30 days' }, { value: '90', label: '90 days' }]}
              />
            </fieldset>

            <div className="mb-5 border-t border-gray-100 pt-4">
              <button
                type="button"
                aria-expanded={guestOptionsOpen}
                aria-controls="client-review-guest-options"
                onClick={() => { capture('client_review_guest_options_toggled', { open: !guestOptionsOpen }); setGuestOptionsOpen(o => !o); }}
                className="w-full flex items-center justify-between gap-2 text-left"
              >
                <span className="min-w-0">
                  <span className="field-label mb-0">Guest options</span>
                  <span className="field-hint block mt-1">{guestSummary}</span>
                </span>
                <HugeiconsIcon icon={ArrowDown01Icon} size={12} color="currentColor" className={`text-gray-400 shrink-0 transition-transform${guestOptionsOpen ? ' rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {guestOptionsOpen && (
                <div id="client-review-guest-options" className="mt-3 space-y-4">
                  <ToggleRow
                    icon={Note01Icon}
                    label="Welcome note"
                    hint="Shown at the top of the comments panel."
                    checked={welcomeOn}
                    disabled={createReview.isPending}
                    onChange={() => { touchGuest(); setWelcomeOn(v => !v); }}
                  >
                    <textarea
                      aria-label="Welcome note text"
                      className="input input-lg w-full h-auto py-2"
                      rows={3}
                      maxLength={WELCOME_NOTE_MAX}
                      placeholder={WELCOME_NOTE_PLACEHOLDER}
                      value={welcomeNote}
                      disabled={createReview.isPending}
                      onChange={(e) => { touchGuest(); setWelcomeNote(e.target.value); }}
                    />
                  </ToggleRow>
                  <ToggleRow
                    icon={ViewOffSlashIcon}
                    label="Keep guest comments private"
                    hint="Each guest sees only their own comments."
                    checked={privateComments}
                    disabled={createReview.isPending}
                    onChange={() => { touchGuest(); setPrivateComments(v => !v); }}
                  />
                  <ToggleRow
                    icon={LaptopPhoneSyncIcon}
                    label="Allow responsive views"
                    hint="Clients can switch between desktop, tablet, and mobile."
                    checked={allowResponsiveViews}
                    disabled={createReview.isPending}
                    onChange={() => { touchGuest(); setAllowResponsiveViews(v => !v); }}
                  />
                  <p className="field-hint">Starts from this project&rsquo;s Sharing settings. Changes here apply to this link&nbsp;only.</p>
                </div>
              )}
            </div>

            {formError && (
              <p className="mb-4 text-sm text-red-600" role="alert">
                {formError}
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                className="btn-secondary whitespace-nowrap"
                onClick={handleClose}
                disabled={createReview.isPending}
              >
                <span className="text-cap-trim">Cancel</span>
              </button>
              <button
                type="submit"
                className="btn-primary whitespace-nowrap"
                disabled={createReview.isPending}
                onClick={() => capture(hasClientEmail ? 'client_review_create_and_send_clicked' : 'client_review_create_link_clicked', {
                  email_count: cleanClientEmails(clientEmails).ok ? (cleanClientEmails(clientEmails) as { emails: string[] }).emails.length : 0,
                })}
              >
                <HugeiconsIcon icon={hasClientEmail ? SentIcon : Link01Icon} size={16} color="currentColor" className="w-4 h-4 mr-2" aria-hidden="true" />
                <span className="text-cap-trim">{createReview.isPending ? 'Creating...' : hasClientEmail ? 'Create and send' : 'Create link'}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  return <ModalPortal>{overlay}</ModalPortal>;
}
import { viewportIconForWidth } from '@overlayqa/shared';
import { ViewportIcon } from '@overlayqa/shared/ui';
